import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { VStack } from '~/components/layout/Stack';
import { useAllBookkeepingAccountsQuery } from '~/services/bookkeeping/bookkeeping-accounts';
import { useVATCodes } from '~/services/bookkeeping/vat-codes';
import { BookkeepingAccount } from '~/types/general-bookkeeping';
import { Money } from '~/types/money';
import { RegistrationPurchaseType, VATBook, VATBookType } from '~/types/vat-bookkeeping';
import { VATCode } from '~/types/vat-bookkeeping';
import { formatPercentageLimitingDecimals } from '~/utils/formatNumber';
import { formatMoney } from '~/utils/money';
import { FixedAssetFormValues } from '~/views/bookkeeping/VAT-registration/fixed-asset/fixedAssetFormSchema';
import styles from './BookkeepingInvoicesEntriesPreviewTable.module.scss';
import BookkeepingInvoicesFixedAssetsPreview, {
  BookkeepingInvoicesPreviewFixedAsset,
} from './BookkeepingInvoicesFixedAssetsPreview';

export interface BookkeepingInvoicesPreviewEntry {
  bookkeepingAccountId: string;
  vatCodeId: string;
  vatPercentage: string;
  taxableAmount: Money;
  taxAmount: Money;
  purchaseType: RegistrationPurchaseType | null;
  fixedAsset?: FixedAssetFormValues[];
}

interface BookkeepingInvoicesFormDataPreviewTableProps {
  entries: BookkeepingInvoicesPreviewEntry[];
  book?: VATBook;
}

const formatBookkeepingAccountLabel = (account: BookkeepingAccount) => {
  return `${account.code} - ${account.name}`;
};
const formatVATCodeLabel = (vatCode: VATCode) => {
  return vatCode.code;
};

const BookkeepingInvoicesEntriesPreviewTable = ({ entries, book }: BookkeepingInvoicesFormDataPreviewTableProps) => {
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'registrations.documents.table.preview' });
  const { t: tPurchaseType } = useTranslation('bookkeeping', { keyPrefix: 'inputs.purchase_type' });

  const { data: accounts } = useAllBookkeepingAccountsQuery({ hasChildren: false });
  const { data: vatCodes } = useVATCodes();

  const showVatDeductibility = book?.bookType === VATBookType.Purchases;

  const fixedAssets = useMemo<BookkeepingInvoicesPreviewFixedAsset[]>(
    () =>
      entries.flatMap((entry) =>
        (entry.fixedAsset ?? []).map((asset) => ({ asset, currency: entry.taxableAmount.currency })),
      ),
    [entries],
  );

  return (
    <VStack gap={2}>
      <table className={styles.table} aria-label={t('title')}>
        <thead>
          <tr>
            <th>{t('bookkeeping_account')}</th>
            <th>{t('vat_code')}</th>
            <th>{t('vat_percentage')}</th>
            {showVatDeductibility && <th>{t('vat_deductibility')}</th>}
            <th className={styles.amount}>{t('taxable_amount')}</th>
            <th className={styles.amount}>{t('vat')}</th>
            {entries.some((entry) => entry.purchaseType) && book?.bookType === VATBookType.Purchases && (
              <th>{t('purchase_type')}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, index) => {
            const account = accounts?.find((a) => a.id === entry.bookkeepingAccountId);
            const vatCode = vatCodes.find((v) => v.id === entry.vatCodeId);

            return (
              <tr key={index}>
                <td>{account ? formatBookkeepingAccountLabel(account) : '-'}</td>
                <td>{vatCode ? formatVATCodeLabel(vatCode) : '-'}</td>
                <td>{formatPercentageLimitingDecimals(Number(entry.vatPercentage))}</td>
                {showVatDeductibility && (
                  <td>
                    {vatCode?.vatDeductibility
                      ? formatPercentageLimitingDecimals(Number(vatCode.vatDeductibility))
                      : '-'}
                  </td>
                )}
                <td className={styles.amount}>{formatMoney(entry.taxableAmount)}</td>
                <td className={styles.amount}>{formatMoney(entry.taxAmount)}</td>
                {book?.bookType === VATBookType.Purchases && entry.purchaseType && (
                  <td>{tPurchaseType(entry.purchaseType)}</td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      <BookkeepingInvoicesFixedAssetsPreview fixedAssets={fixedAssets} />
    </VStack>
  );
};

export default BookkeepingInvoicesEntriesPreviewTable;
